import { ActionIcon, Badge, Group, Menu, Text, Title, Tooltip } from '@mantine/core';
import { IconClock, IconDots, IconUser } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { unwrap } from '../api/client';
import { assignConversation, getContact, listMembers, setConversationStatus, snoozeConversation } from '../api/gen';
import type { Conversation } from '../api/gen';
import { listQuery } from '../api/list';
import { useOrg } from '../api/org';
import { since, when } from '../lib/format';

/** Who this is, and the four things an operator does to a thread. */
export function ThreadHeader({ conversation }: { conversation: Conversation }) {
  const org = useOrg();
  const qc = useQueryClient();

  const contact = useQuery({
    queryKey: ['contact', conversation.contactId],
    queryFn: () => unwrap(getContact({ path: { id: conversation.contactId }, headers: org })),
  });
  const members = useQuery({
    queryKey: ['members'],
    queryFn: () => unwrap(listMembers({ headers: org, query: listQuery('members', { limit: 100 }) })),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['conversations'] });
    void qc.invalidateQueries({ queryKey: ['conversation', conversation.id] });
  };

  const status = useMutation({
    mutationFn: (next: 'open' | 'closed') =>
      unwrap(setConversationStatus({ path: { id: conversation.id }, headers: org, body: { status: next } })),
    onSuccess: invalidate,
  });
  const assign = useMutation({
    mutationFn: (body: { assignee: 'unassigned' | 'agent' | 'member'; memberId?: string }) =>
      unwrap(assignConversation({ path: { id: conversation.id }, headers: org, body })),
    onSuccess: invalidate,
  });
  const snooze = useMutation({
    mutationFn: (hours: number) =>
      unwrap(snoozeConversation({ path: { id: conversation.id }, headers: org, body: { hours } })),
    onSuccess: invalidate,
  });

  const who = contact.data?.displayName || contact.data?.email || 'Customer';

  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <div>
        <Title order={5} lineClamp={1}>
          {conversation.subject || 'No subject'}
        </Title>
        <Group gap="xs" mt={2}>
          <Text size="xs" c="dimmed">
            {who}
          </Text>
          <Badge size="xs" variant="default">
            {conversation.channel}
          </Badge>
          {conversation.awaitingSince ? (
            <Tooltip label="How long the customer has been waiting for an answer">
              <Badge size="xs" color="orange" variant="light">
                waiting {since(conversation.awaitingSince)}
              </Badge>
            </Tooltip>
          ) : null}
          {conversation.snoozedUntil ? (
            <Badge size="xs" color="gray" variant="light">
              back {when(conversation.snoozedUntil)}
            </Badge>
          ) : null}
        </Group>
      </div>

      <Group gap="xs" wrap="nowrap">
        <Menu>
          <Menu.Target>
            <ActionIcon variant="subtle" aria-label="Assign">
              <IconUser size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Assign to</Menu.Label>
            <Menu.Item onClick={() => assign.mutate({ assignee: 'unassigned' })}>Nobody</Menu.Item>
            <Menu.Item onClick={() => assign.mutate({ assignee: 'agent' })}>The agent</Menu.Item>
            {(members.data?.data ?? []).map((m) => (
              <Menu.Item key={m.id} onClick={() => assign.mutate({ assignee: 'member', memberId: m.id })}>
                {m.displayName || m.subject}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>

        <Menu>
          <Menu.Target>
            <ActionIcon variant="subtle" aria-label="Snooze">
              <IconClock size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Bring it back in</Menu.Label>
            {[
              { label: '3 hours', hours: 3 },
              { label: 'tomorrow', hours: 24 },
              { label: 'three days', hours: 72 },
              { label: 'a week', hours: 168 },
            ].map((o) => (
              <Menu.Item key={o.hours} onClick={() => snooze.mutate(o.hours)}>
                {o.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>

        <Menu>
          <Menu.Target>
            <ActionIcon variant="subtle" aria-label="More">
              <IconDots size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {conversation.status === 'closed' ? (
              <Menu.Item onClick={() => status.mutate('open')}>Reopen</Menu.Item>
            ) : (
              <Menu.Item onClick={() => status.mutate('closed')}>Close</Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
}
